import PropTypes from 'prop-types'
import { cn } from '../../lib/cn'

/**
 * Input component with label and error state
 *
 * @component
 * @example
 * <Input
 *   label="Email"
 *   type="email"
 *   value={email}
 *   onChange={handleChange}
 *   error="Email is required"
 * />
 */
export default function Input({
  label,
  type = 'text',
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  disabled = false,
  required = false,
  autoFocus = false,
  icon,
  className = '',
  ...props
}) {
  const inputId = name || label?.toLowerCase().replace(/\s+/g, '-')

  const inputClasses = cn(
    'block w-full rounded-lg border px-4 py-2.5 text-base transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500',
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
    icon && 'pl-11',
    className
  )

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}

      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {icon}
          </div>
        )}

        <input
          id={inputId}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
      </div>

      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}

Input.propTypes = {
  /** Input label */
  label: PropTypes.string,
  /** Input type */
  type: PropTypes.oneOf(['text', 'email', 'password', 'tel', 'number', 'search', 'url']),
  /** Input name attribute */
  name: PropTypes.string,
  /** Input value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Change handler */
  onChange: PropTypes.func,
  /** Blur handler */
  onBlur: PropTypes.func,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Error message */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Required field */
  required: PropTypes.bool,
  /** Auto focus */
  autoFocus: PropTypes.bool,
  /** Optional icon element */
  icon: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string
}
